import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UpdateWindowService } from '../services/updateWindowService';
import { CreateUpdateWindowData, EditUpdateWindowData, UpdateWindow } from '../models/updateWindow';

// Query Keys
const UPDATE_WINDOWS_KEYS = {
    all: ['updateWindows'] as const,
    lists: () => [...UPDATE_WINDOWS_KEYS.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...UPDATE_WINDOWS_KEYS.lists(), filters] as const,
    active: () => [...UPDATE_WINDOWS_KEYS.all, 'active'] as const,
    permission: (collaboratorId?: string) => [...UPDATE_WINDOWS_KEYS.all, 'permission', collaboratorId] as const,
};

// Hook para buscar todas as janelas de atualização
export function useUpdateWindowsQuery() {
    return useQuery({
        queryKey: UPDATE_WINDOWS_KEYS.list(),
        queryFn: () => UpdateWindowService.getAll(),
        staleTime: 5 * 60 * 1000, // 5 minutos
    });
}

// Hook para buscar janelas de atualização ativas
export function useActiveUpdateWindowsQuery() {
    return useQuery({
        queryKey: UPDATE_WINDOWS_KEYS.active(),
        queryFn: () => UpdateWindowService.getActiveWindows(),
        staleTime: 1 * 60 * 1000, // 1 minuto (mais frequente para janelas ativas)
    });
}

// Hook para verificar se colaborador pode atualizar perfil
export function useCanUpdateProfileQuery(collaboratorId?: string) {
    return useQuery({
        queryKey: UPDATE_WINDOWS_KEYS.permission(collaboratorId),
        queryFn: () => UpdateWindowService.canUpdateProfile(collaboratorId),
        staleTime: 30 * 1000, // 30 segundos
        enabled: !!collaboratorId,
    });
}

// Hook para criar janela de atualização
export function useCreateUpdateWindowMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: CreateUpdateWindowData) => UpdateWindowService.create(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: UPDATE_WINDOWS_KEYS.all });
        },
    });
}

// Hook para atualizar janela de atualização
export function useUpdateUpdateWindowMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: string; data: Partial<EditUpdateWindowData> }) => 
            UpdateWindowService.update(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: UPDATE_WINDOWS_KEYS.all });
        },
    });
}

// Hook para deletar janela de atualização
export function useDeleteUpdateWindowMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => UpdateWindowService.delete(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: UPDATE_WINDOWS_KEYS.all });
        },
    });
}

// Hook para verificar se há janela ativa (versão simples)
export function useHasActiveUpdateWindow() {
    const { data: activeWindows, isLoading, error } = useActiveUpdateWindowsQuery();

    return {
        hasActiveWindow: (activeWindows?.length || 0) > 0,
        activeWindow: activeWindows?.[0],
        isLoading,
        error
    };
}